import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../../../../@types/SurveyBuilder";
import { ILanguageResponse } from "../../../../../@types/Translations";
import { SelectDropdown } from "../../../../../components/SelectDropdown";
import { useModal } from "../../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../../utils/dateFormatter";
import { LiveHistoryModal } from "../LiveHistoryModal/LiveHistoryModal";
import { HistoryLink } from "../LiveHistoryModal/LiveHistoryModal.styles";
import {
  Badge,
  BarGroup,
  BarRow,
  HelpCard,
  HelpIcon,
  HelpLive,
  HelpNote,
  HelpStates,
  IssuesList,
  LiveDot,
  VersionBarContainer,
} from "./VersionBar.styles";

interface IVersionBarProps {
  survey: ISurveyBuilderSurvey;
  languageCode: string;
  languages: ILanguageResponse[];
  issues: string[];
  onVersionChange(versionId: string): void;
  onLanguageChange(languageCode: string): void;
}

type VersionState = "draft" | "live" | "previous";

/**
 * EN-5261: which version is open (the draft is the only editable one), in which language, and which
 * version patients answer since when. The version actions (publish, create/discard draft, make live)
 * are in the builder header.
 */
export function VersionBar({
  survey,
  languageCode,
  languages,
  issues,
  onVersionChange,
  onLanguageChange,
}: IVersionBarProps) {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { version } = survey;

  // One state per version: Draft (editable), Live (patients answer it) or Previous (any other published one).
  const stateOf = (status: string, live: boolean): VersionState => {
    if (status === "Draft") return "draft";
    return live ? "live" : "previous";
  };
  const stateLabel = (state: VersionState) => {
    if (state === "draft") return t("surveys_builder_versions_draft");
    if (state === "live") return t("surveys_builder_versions_live");
    return t("surveys_builder_versions_previous");
  };

  const openState = stateOf(
    version.status,
    survey.liveVersionId === version.id
  );
  const live = survey.versions.find((v) => v.id === survey.liveVersionId);

  const versionOptions = [...survey.versions]
    .sort((a, b) => b.number - a.number)
    .map((v) => ({
      key: v.id,
      value: v.id,
      label: t("surveys_builder_versions_option", {
        number: v.number,
        status: stateLabel(stateOf(v.status, v.isLive)),
      }),
    }));

  const languageOptions = languages.map((language) => ({
    key: language.code,
    value: language.code,
    label: language.name ?? language.code,
  }));

  let readOnlyMessage: string | undefined;
  if (openState === "live") {
    readOnlyMessage = survey.draftVersionId
      ? t("surveys_builder_versions_readOnlyDraftExists")
      : t("surveys_builder_versions_readOnly");
  } else if (openState === "previous") {
    readOnlyMessage = t("surveys_builder_versions_readOnlyPrevious", {
      number: live?.number,
    });
  }

  return (
    <VersionBarContainer>
      <BarRow>
        <BarGroup>
          <SelectDropdown
            options={versionOptions}
            value={version.id}
            width={240}
            onValueChange={(value) => onVersionChange(value as string)}
          />
          <Badge tone={openState}>{stateLabel(openState)}</Badge>
          <Tooltip
            placement="bottomLeft"
            color="white"
            styles={{ body: { padding: 0, width: 400 } }}
            title={
              <HelpCard>
                <HelpLive>
                  <LiveDot active={!!live} />
                  {live
                    ? t("surveys_builder_versions_liveSince", {
                        number: live.number,
                        date: survey.liveSince
                          ? getLongFormattedDate(survey.liveSince)
                          : "—",
                      })
                    : t("surveys_builder_versions_notLive")}
                </HelpLive>
                <HelpStates>
                  {(["draft", "live", "previous"] as VersionState[]).map(
                    (state) => (
                      <li key={state}>
                        <Badge tone={state}>{stateLabel(state)}</Badge>
                        <span>
                          {t(`surveys_builder_versions_help_${state}`)}
                        </span>
                      </li>
                    )
                  )}
                </HelpStates>
                {readOnlyMessage && <HelpNote>{readOnlyMessage}</HelpNote>}
              </HelpCard>
            }
          >
            <HelpIcon
              type="button"
              aria-label={t("surveys_builder_versions_helpLabel")}
            >
              <InfoCircledIcon width={16} height={16} />
            </HelpIcon>
          </Tooltip>
          {survey.liveHistory?.length > 0 && (
            <HistoryLink
              type="button"
              onClick={() =>
                openModal(<LiveHistoryModal history={survey.liveHistory} />, {
                  width: "640px",
                  height: "auto",
                  showCloseButton: true,
                })
              }
            >
              {t("surveys_builder_history_open")}
            </HistoryLink>
          )}
        </BarGroup>
        <SelectDropdown
          options={languageOptions}
          value={languageCode}
          width={220}
          onValueChange={(value) => onLanguageChange(value as string)}
        />
      </BarRow>

      {issues.length > 0 && (
        <IssuesList role="alert">
          <strong>{t("surveys_builder_publish_blocked")}</strong>
          <ul>
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </IssuesList>
      )}
    </VersionBarContainer>
  );
}
